import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import * as DateUtils from './DateUtils';

/**
 * @description Determine if is a valid date range
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const isValidRange = (startDate, endDate) => {
  if (startDate === undefined && endDate === undefined) {
    return true;
  }
  if (!startDate || !endDate) {
    return false;
  }
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.isBefore(end, 'date') || start.isSame(end, 'date');
};

export const orderRange = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (start.isBefore(end, 'date')) {
    return [startDate, endDate];
  }
  return [endDate, startDate];
};

/**
 * @description Function to parse a date
 * @param {string} str
 * @param {string} format
 */
export const parseDate = (str, format) => {
  dayjs.extend(customParseFormat);
  const parsed = dayjs(str, format).toDate();
  if (DateUtils.isDate(parsed)) {
    return parsed;
  }
  return undefined;
};

/**
 * @description Determine if an input date is valid
 * @param {string} name
 * @param {*} value
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const isValidValue = (name, value, startDate, endDate) => {
  let result = true;
  if (startDate || endDate) {
    result = name === 'from' ? isValidRange(value, endDate) : isValidRange(startDate, value);
  }
  return result;
};

/**
 * @description Format a date obj
 * @param {Date} date
 * @param {string} format
 */
export const formatDate = (date, format, locale = null) => {
  if (locale) {
    return dayjs(date).locale(locale, null, true).format(format);
  }
  return dayjs(date).format(format);
};

/**
 * @description Given a date fromat find his separator
 * @param {string} format
 */
export const searchSeparator = (format) => {
  let separator = '';
  for (let index = 0; index < format.length; index += 1) {
    if (format.charCodeAt(index) >= 45 && format.charCodeAt(index) <= 47) {
      separator = format.charAt(index);
    }
  }
  return separator;
};

export function isSameDay(first, second) {
  if (!first || !second) {
    return false;
  }
  return DateUtils.isSameDay(first, second);
}

export function isSameDateRange(first, second) {
  if (!first || !second) {
    return false;
  }
  return (
    ((!first.start && !second.start) || DateUtils.isSameDay(first.start, second.start)) &&
    ((!first.end && !second.end) || DateUtils.isSameDay(first.end, second.end))
  );
}

export function isDayInRange(day, range) {
  if (!day || !range || !range.from || !range.to) {
    return false;
  }
  return DateUtils.isDayInRange(day, range);
}

export function setCursorPositionToInput(input, pos) {
  if (input.createTextRange) {
    const part = input.createTextRange();
    part.move('character', pos);
    part.select();
  } else if (input.setSelectionRange) {
    input.setSelectionRange(pos, pos);
  }
  input.focus();
}

export function focusInput(input) {
  if (input.getInput) {
    input.getInput().focus();
  } else {
    input.focus();
  }
}

export function focus(ref) {
  if (ref.current) {
    if (ref.current.getInput) {
      ref.current.getInput().focus();
    } else {
      ref.current.focus();
    }
  }
}

export function blur(ref) {
  if (ref.current) {
    if (ref.current.getInput) {
      ref.current.getInput().blur();
    } else {
      ref.current.blur();
    }
  }
}

export function isDayBefore(day1, day2) {
  if (!day1 || !day2) {
    return false;
  }
  return DateUtils.isDayBefore(day1, day2);
}

export function isDayAfter(day1, day2) {
  if (!day1 || !day2) {
    return false;
  }
  return DateUtils.isDayAfter(day1, day2);
}

export function isDayAfterOrSame(day1, day2) {
  if (!day1 || !day2) {
    return false;
  }
  return DateUtils.isDayAfter(day1, day2) || DateUtils.isSameDay(day1, day2);
}

export function isPastDay(date) {
  if (!date) {
    return false;
  }
  return DateUtils.isPastDay(date);
}

export function isFutureOrPresentDay(date) {
  if (!date) {
    return false;
  }
  return !DateUtils.isPastDay(date);
}

export function appendValue(source, position, value) {
  return source.indexOf(value, position) - 1 === position ? value : '';
}

export function isDate(date) {
  if (!date) {
    return false;
  }
  return DateUtils.isDate(date);
}

export const computeDateRange = (inputObj) => {
  if (!inputObj) {
    return {};
  }
  const dateRange = {};
  const today = dayjs();
  Object.keys(inputObj).forEach((key) => {
    if (typeof inputObj[key] === 'number') {
      // If it is the number the resulting date is today + <number> of days
      dateRange[key] = today.add(inputObj[key], 'day').toDate();
    } else if (typeof inputObj[key] === 'string') {
      // If it is a string, it is asumed to be a date to be parsed
      dateRange[key] = dayjs(inputObj[key]).toDate();
    } else {
      // In any other case it will be left as is
      dateRange[key] = inputObj[key];
    }
  });
  if (
    Boolean(dateRange.start) &&
    Boolean(dateRange.end) &&
    dayjs(dateRange.start).isValid() &&
    dayjs(dateRange.end).isValid() &&
    isDayAfterOrSame(dateRange.end, dateRange.start) &&
    isDayAfterOrSame(dateRange.start, dayjs().toDate()) &&
    isDayAfterOrSame(dateRange.end, dayjs().toDate())
  ) {
    return dateRange;
  } else if (
    Boolean(dateRange.start) &&
    dateRange.end === null &&
    dayjs(dateRange.start).isValid() &&
    isDayAfterOrSame(dateRange.start, dayjs().toDate())
  ) {
    return dateRange;
  } else {
    return {};
  }
};

/**
 * Execute a function on the next tick
 * @param {function} function
 */
export const nextTick = (callback) => {
  setTimeout(() => callback(), 0);
};
